"use client";

import { Loader2, Trash2 } from "lucide-react";
import { createElement, type ReactNode } from "react";
import { Alert } from "../../elements/alert/alert";
import { Button } from "../../elements/button/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../elements/dialog/dialog";
import { Text } from "../../elements/text/text";
import { cn } from "../../lib/utils";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  titleAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleClassName?: string;
  subtitle?: string;
  headerCentered?: boolean;
  children: ReactNode;
  primaryButtonText?: string;
  isPrimaryButtonDisabled?: boolean;
  formId?: string;
  secondaryButtonText?: string;
  onPrimaryAction?: () => void;
  onSecondaryAction?: () => void;
  primaryButtonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  secondaryButtonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  preventCloseOnOutsideClick?: boolean;
  preventAutoFocus?: boolean;
  modalClassName?: string;
  contentClassName?: string;
  showDeleteButton?: boolean;
  onDelete?: () => void;
  deleteButtonText?: string;
  isLoading?: boolean;
  generalErrorMessage?: string;
  tertiaryButtonText?: string;
  onTertiaryAction?: () => void;
  isTertiaryButtonDisabled?: boolean;
  tertiaryButtonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
};

function ModalFooter(
  props: Readonly<
    Pick<
      ModalProps,
      | "showDeleteButton"
      | "deleteButtonText"
      | "isLoading"
      | "onDelete"
      | "secondaryButtonText"
      | "secondaryButtonVariant"
      | "onSecondaryAction"
      | "onClose"
      | "tertiaryButtonText"
      | "tertiaryButtonVariant"
      | "isTertiaryButtonDisabled"
      | "onTertiaryAction"
      | "primaryButtonText"
      | "formId"
      | "isPrimaryButtonDisabled"
      | "onPrimaryAction"
      | "primaryButtonVariant"
    >
  >
) {
  return (
    <DialogFooter>
      <div className="flex w-full items-center justify-between px-2">
        {/* Left Side: Delete Button */}
        {props.showDeleteButton ? (
          <Button
            className="flex items-center gap-2 text-red-600 text-sm hover:text-red-800"
            disabled={props.isLoading}
            onClick={props.onDelete}
            variant="outline"
          >
            <Trash2 className="h-4 w-4" />
            {props.deleteButtonText}
          </Button>
        ) : (
          <div />
        )}

        {/* Right Side: Cancel + Save */}
        <div className="flex gap-3">
          {props.secondaryButtonText ? (
            <Button
              disabled={props.isLoading}
              onClick={props.onSecondaryAction || props.onClose}
              variant={props.secondaryButtonVariant || "outline"}
            >
              {props.secondaryButtonText}
            </Button>
          ) : null}
          {props.tertiaryButtonText ? (
            <Button
              disabled={props.isLoading || props.isTertiaryButtonDisabled}
              onClick={props.onTertiaryAction}
              variant={props.tertiaryButtonVariant || "default"}
            >
              {props.tertiaryButtonText}
            </Button>
          ) : null}

          {props.primaryButtonText ? (
            <Button
              className="relative"
              disabled={props.isPrimaryButtonDisabled || props.isLoading}
              form={props.formId}
              onClick={props.onPrimaryAction}
              type={props.formId ? "submit" : "button"}
              variant={props.primaryButtonVariant || "default"}
            >
              <span className={props.isLoading ? "invisible" : ""}>
                <Text>{props.primaryButtonText}</Text>
              </span>

              {props.isLoading ? (
                <span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              ) : null}
            </Button>
          ) : null}
        </div>
      </div>
    </DialogFooter>
  );
}

export function Modal(props: Readonly<ModalProps>) {
  const handleOpenChange = (open: boolean) => {
    if (!(open || props.preventCloseOnOutsideClick)) {
      props.onClose();
    }
  };

  const headerCentered = props.headerCentered ?? true;

  return (
    <Dialog onOpenChange={handleOpenChange} open={props.isOpen}>
      <DialogContent
        className={cn(props.modalClassName ?? "max-w-2xl p-6 md:max-w-4xl")}
        onInteractOutside={(e) => {
          if (props.preventCloseOnOutsideClick) {
            e.preventDefault();
          }
        }}
        onOpenAutoFocus={
          props.preventAutoFocus ? (e) => e.preventDefault() : undefined
        }
      >
        <div className={cn("flex max-h-[90vh] flex-col gap-4")}>
          {/* Title */}
          <DialogHeader
            className={cn(headerCentered ? "text-center" : "text-left")}
          >
            <DialogTitle
              asChild={!!props.titleAs}
              className={cn(
                props.subtitle ? "text-xl" : "text-2xl",
                props.titleClassName ??
                  "font-semibold text-neutral-darker leading-8 tracking-tight"
              )}
            >
              {props.titleAs
                ? createElement(props.titleAs, {}, props.title)
                : props.title}
            </DialogTitle>
            {props.subtitle ? (
              <Text color="muted" variant="body-sm">
                {props.subtitle}
              </Text>
            ) : null}
          </DialogHeader>

          {/* General Error Message */}
          {props.generalErrorMessage ? (
            <Alert
              microcopies={{
                title: props.generalErrorMessage,
              }}
              variant="destructive"
            />
          ) : null}

          {/* Content */}
          <div
            className={
              props.contentClassName ??
              "max-h-[50vh] min-h-0 flex-1 overflow-y-auto"
            }
          >
            {props.children}
          </div>

          {/* Action buttons */}
          {props.primaryButtonText ||
          props.secondaryButtonText ||
          props.showDeleteButton ? (
            <ModalFooter {...props} />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
